import { Field, Textarea } from '@/components/form';
import { Input } from '@/components/ui/input';
import { hasArabicLetter } from '@/lib/arabic';
import { useT } from '@/lib/i18n';

/**
 * An Arabic name or description (document 15, section 4.5): typed right to left in the Arabic
 * face, under its English counterpart. `dir="auto"` on descriptions lets a Latin brand name read
 * correctly inside Arabic text.
 */
export function ArabicField({
  label,
  value,
  onChange,
  maxLength = 100,
  error,
  multiline = false,
  what = 'name',
}: {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  maxLength?: number;
  error?: string | undefined;
  multiline?: boolean;
  what?: string;
}) {
  const t = useT();
  const hint =
    value.trim() && !hasArabicLetter(value)
      ? t('arabicField.noArabic')
      : what === 'name'
        ? t('arabicField.hintName')
        : t('arabicField.hintDescription');
  return (
    <Field label={label ?? t('arabicField.name')} error={error} hint={hint}>
      {(id, describedBy) =>
        multiline ? (
          <Textarea
            id={id}
            dir="auto"
            lang="ar"
            value={value}
            maxLength={maxLength}
            aria-describedby={describedBy}
            onChange={(e) => onChange(e.target.value)}
          />
        ) : (
          <Input
            id={id}
            dir="rtl"
            lang="ar"
            value={value}
            maxLength={maxLength}
            aria-invalid={Boolean(error)}
            aria-describedby={describedBy}
            onChange={(e) => onChange(e.target.value)}
          />
        )
      }
    </Field>
  );
}
